import React, { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import './send-recommended-notification-editor.scss'
import { Well } from '@pushly/aqe/lib/components'
import { useRefState, useService } from '@pushly/aqe/lib/hooks'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import {
    JourneyNode,
    NodeEditorRef,
    SendNotificationActionStep,
    SendRecommendedNotificationActionStep,
} from '../../types/journey-nodes'
import { Alert, Button, Cascader, Divider, Skeleton, Tabs } from 'antd'
import {
    BlueConicRecommendedNotificationIntegration,
    BlueConicRecommendedNotificationParams,
    isBlueConicRecommendedNotificationParams,
} from '@pushly/models/lib/structs/journeys'
import { RecommendationSource } from '@pushly/models/lib/enums/recommendations/recommendation-source'
import { GetBlueConicIntegrationResponse, GetDomainIntegrationResponse } from '@pushly/models/lib/structs/domains'
import { DomainIntegrationProviders } from '@pushly/models/lib/enums/domain-integration-providers'
import { PropertiesOf } from '@pushly/models/lib/types'
import { getWrappedMacro, Macro, PlatformIntegrationOwnerType } from '@pushly/models/lib/enums'
import { useJourneyContext } from '../../context'
import { BlueConicRecommendationEditor } from './blueconic/blueconic-recommendation-editor'
import { BLUECONIC_DISPLAY_VALUE, MAX_BLUECONIC_RECOMMENDATION_CONIGS_ALLOWED } from './blueconic/constants'
import { Loading3QuartersOutlined } from '@ant-design/icons'
import { DomainIntegrationService } from '../../../../services'
import { validateRecommendedNotification } from './helpers'
import { alphaSort } from '../../../../_utils/array'
import classnames from 'classnames'
import { JourneyBuilderMode } from '../../enums'
import { SendNotificationEditor } from '../send-notification-editor'
import { JourneyAction } from '../../types/journey-context'
import { NotificationVariantModel } from '../../../../models/notification/notification-variant.model'

type SendRecommendedNotificationEditorProps = {
    node: JourneyNode<SendRecommendedNotificationActionStep>
    ref: ForwardedRef<NodeEditorRef>
}

type Option = {
    value: string | number
    label: React.ReactNode
    children?: Option[]
}

type ActiveTabKey = 'configuration' | 'notification'

export const SendRecommendedNotificationEditor = forwardRef<NodeEditorRef, SendRecommendedNotificationEditorProps>(
    ({ node }, ref) => {
        const domainIntegrationSvc = useService(DomainIntegrationService)
        const [state, dispatch] = useJourneyContext()

        const [loadingIntegrations, setLoadingIntegrations] = useState(false)
        const [sourceOptions, setSourceOptions] = useState<Option[]>([])
        const [activeTab, setActiveTab] = useState<ActiveTabKey>('configuration')

        const notifEditorRef = useRef<NodeEditorRef>(null)
        const handleSubmitRef = useRef<
            (() => Promise<JourneyNode<SendRecommendedNotificationActionStep> | void>) | null
        >(null)

        const [localValueRef, setLocalValue] = useRefState<BlueConicRecommendedNotificationParams>(
            isBlueConicRecommendedNotificationParams(node.data.step.configuration.params)
                ? node.data.step.configuration.params.clone()
                : BlueConicRecommendedNotificationParams.parseJsonObject(node.data.step.configuration.params),
        )
        const localValue = localValueRef.current

        const onChange = (change: PropertiesOf<BlueConicRecommendedNotificationParams>) => {
            setLocalValue(localValue.clone(change))
        }

        handleSubmitRef.current = async (): Promise<JourneyNode<SendRecommendedNotificationActionStep> | void> => {
            const configurationUpdates = await handleConfigurationSubmit(localValue)

            if (configurationUpdates) {
                const newNode = { ...node }

                // only reset invalid node state if store has actually changed
                if (node.data.uiMeta.invalid) {
                    if (
                        'integration' in configurationUpdates &&
                        configurationUpdates.integration.store_id !==
                            newNode.data.step.configuration.params.integration.store_id
                    ) {
                        newNode.data.setUIMeta({ invalid: undefined })
                    }
                }

                newNode.data.step.configuration.params = configurationUpdates

                const notificationUpdates = await handleNotificationSubmit(notifEditorRef.current)
                if (!notificationUpdates) {
                    return
                }
                newNode.data.step.configuration.params.notification = notificationUpdates

                return newNode
            }
        }

        useImperativeHandle(ref, () => ({ onSubmit: handleSubmitRef.current! }))

        useEffect(() => {
            const getRecommendationIntegrations = async () => {
                setLoadingIntegrations(true)
                let integrations: Map<number, GetDomainIntegrationResponse> =
                    state.dependencies?.integrations ?? new Map()
                if (!integrations.size) {
                    const svcRes = await domainIntegrationSvc.fetchAll(state.domain.id)

                    if (svcRes.ok && svcRes.data?.length) {
                        integrations = new Map(
                            svcRes.data.map<[number, GetDomainIntegrationResponse]>((resInt) => [
                                resInt.id,
                                GetDomainIntegrationResponse.parseJsonObject(resInt),
                            ]),
                        )
                    }
                }

                const blueConicSourceOptions = Array.from(integrations.values())
                    .filter((datum) => datum.provider === DomainIntegrationProviders.BLUECONIC_REC)
                    .map((datum) => ({
                        label: (datum.data as GetBlueConicIntegrationResponse).name,
                        value: datum.id,
                        children: Object.keys((datum.data as GetBlueConicIntegrationResponse).stores)
                            .map((store_id) => ({
                                label: ((datum.data as GetBlueConicIntegrationResponse).stores[store_id] as any).name,
                                value: store_id,
                            }))
                            .sort(({ label: aLabel }, { label: bLabel }) => alphaSort(aLabel, bLabel)),
                    }))
                    .sort(({ label: aLabel }, { label: bLabel }) => alphaSort(aLabel, bLabel))

                setSourceOptions([
                    {
                        label: (
                            <span className="integration-label">
                                <span className="integration-label-source">{BLUECONIC_DISPLAY_VALUE}</span>
                            </span>
                        ),
                        value: RecommendationSource.BLUECONIC,
                        children: blueConicSourceOptions,
                    },
                ])

                setLoadingIntegrations(false)
            }

            getRecommendationIntegrations()
        }, [])

        useEffect(() => {
            dispatch({
                action: JourneyAction.SET_EDITOR_FOOTER,
                data: (
                    <>
                        {activeTab === 'notification' && (
                            <Button
                                type="default"
                                onClick={() =>
                                    handleTabChange(
                                        'configuration',
                                        activeTab,
                                        localValueRef,
                                        notifEditorRef,
                                        setActiveTab,
                                    )
                                }
                            >
                                Back: Configuration
                            </Button>
                        )}
                        <Button
                            type="primary"
                            onClick={async () => {
                                if (activeTab === 'configuration') {
                                    await handleTabChange(
                                        'notification',
                                        activeTab,
                                        localValueRef,
                                        notifEditorRef,
                                        setActiveTab,
                                    )
                                    return
                                }

                                const nodeChange = await handleSubmitRef.current!()
                                if (nodeChange) {
                                    dispatch({ action: JourneyAction.ON_NODE_UPDATE, data: { updates: nodeChange } })
                                    dispatch({ action: JourneyAction.SET_ACTIVE_NODE, data: null })
                                }
                            }}
                        >
                            {activeTab === 'notification' ? 'Submit Step' : 'Next: Notification'}
                        </Button>
                    </>
                ),
            })
        }, [activeTab])

        return (
            <div id="send-recommended-notification-editor" className="send-recommended-notification-editor">
                {state.mode === JourneyBuilderMode.READONLY && (
                    <>
                        <Alert type="info" message="You are currently viewing this step in View-Only Mode." showIcon />
                        <Divider type="horizontal" />
                    </>
                )}
                <Well className="recommendation-source-section" mode="ghost" hideFooter={true} title="Source">
                    <Cascader<Option>
                        className={classnames(state.mode === JourneyBuilderMode.READONLY ? 'view-only' : undefined)}
                        placeholder={
                            loadingIntegrations ? (
                                <Loading3QuartersOutlined spin={true} />
                            ) : (
                                'Select a Recommendation Integration Source'
                            )
                        }
                        disabled={loadingIntegrations}
                        popupClassName="integration-cascader-popup"
                        value={
                            localValue?.recommendation_source
                                ? [
                                      localValue?.recommendation_source,
                                      localValue?.integration?.integration_id,
                                      localValue?.integration?.store_id,
                                  ]
                                : undefined
                        }
                        allowClear={false}
                        options={sourceOptions}
                        expandTrigger="hover"
                        onChange={([recommendation_source, integration_id, store_id]) => {
                            onChange({
                                recommendation_config: localValue.recommendation_config,
                                recommendation_source: recommendation_source as RecommendationSource.BLUECONIC,
                                integration: BlueConicRecommendedNotificationIntegration.from({
                                    integration_owner_type: PlatformIntegrationOwnerType.DOMAIN,
                                    integration_id: parseInt(String(integration_id), 10),
                                    store_id: String(store_id),
                                }),
                                notification: localValue.notification,
                                notification_id: localValue.notification_id,
                            })
                        }}
                    />
                </Well>

                <Tabs
                    activeKey={activeTab}
                    onChange={(next: ActiveTabKey) =>
                        handleTabChange(next, activeTab, localValueRef, notifEditorRef, setActiveTab)
                    }
                    items={[
                        {
                            key: 'configuration',
                            label: (
                                <div>
                                    Configurations &nbsp;
                                    {localValue?.recommendation_source === RecommendationSource.BLUECONIC && (
                                        <span className="size-label">
                                            ({localValue.recommendation_config?.length}/
                                            {MAX_BLUECONIC_RECOMMENDATION_CONIGS_ALLOWED})
                                        </span>
                                    )}
                                </div>
                            ),
                            children: (
                                <Well
                                    className="recommendation-configurations-builder-section"
                                    mode="ghost"
                                    hideFooter={true}
                                    title="Recommendation Configurations"
                                >
                                    {loadingIntegrations ? (
                                        <Skeleton paragraph={{ rows: 10 }} active loading={loadingIntegrations} />
                                    ) : !localValue ||
                                      !localValue?.recommendation_source ||
                                      !localValue?.integration.integration_id ||
                                      !localValue?.integration.store_id ? (
                                        <span>
                                            Please select a Recommendation Integration Source to continue configuring
                                            recommendations
                                        </span>
                                    ) : localValue?.recommendation_source === RecommendationSource.BLUECONIC ? (
                                        <BlueConicRecommendationEditor
                                            readonly={state.mode === JourneyBuilderMode.READONLY}
                                            domain={state.domain}
                                            integration_id={123456}
                                            blueConicRecommendation={localValue}
                                            onChange={onChange}
                                            ref={ref}
                                        />
                                    ) : null}
                                </Well>
                            ),
                        },
                        {
                            key: 'notification',
                            label: 'Notification Setup',
                            children: (
                                <SendNotificationEditor
                                    node={node}
                                    ref={notifEditorRef}
                                    builderProps={{
                                        macroProps: {
                                            categories: {
                                                urlFields: [
                                                    'domain',
                                                    'device',
                                                    'location',
                                                    'article_recommendations',
                                                    'notification',
                                                    'custom',
                                                ],
                                                messageFields: ['location', 'article_recommendations', 'custom'],
                                            },
                                            imageMacroProps: {
                                                tag: Macro.ARTICLE_IMAGE,
                                                label: 'Use article image',
                                            },
                                        },
                                        onInitialVariantInit: (variant: NotificationVariantModel) => {
                                            const content = variant.getContent().getDefaultContent()

                                            let landingUrl = getWrappedMacro(Macro.ARTICLE_URL)
                                            const title = getWrappedMacro(Macro.ARTICLE_TITLE)
                                            const body = getWrappedMacro(Macro.ARTICLE_DESCRIPTION, {
                                                defaultValue: '',
                                            })
                                            const imageUrl = getWrappedMacro(Macro.ARTICLE_IMAGE)

                                            switch (localValue.recommendation_source) {
                                                case RecommendationSource.BLUECONIC: {
                                                    landingUrl = getWrappedMacro(Macro.ARTICLE_TRACKING_URL)
                                                    break
                                                }
                                            }

                                            content.setLandingUrl(landingUrl)
                                            content.setTitle(title)
                                            content.setBody(body)
                                            content.setImageUrl(imageUrl)
                                        },
                                    }}
                                />
                            ),
                        },
                    ]}
                />
            </div>
        )
    },
)

async function handleNotificationSubmit(nodeEditor: NodeEditorRef | null) {
    const notificationUpdates = (await nodeEditor?.onSubmit()) as JourneyNode<SendNotificationActionStep>
    return notificationUpdates?.data.step.configuration.params.notification
}

async function handleConfigurationSubmit(config: BlueConicRecommendedNotificationParams) {
    const [isValid, errorMsg] = validateRecommendedNotification(config)
    if (!isValid) {
        simpleNotification('error', errorMsg)
        return null
    }

    return config
}

async function handleTabChange(
    nextTabKey: ActiveTabKey,
    currTabKey: ActiveTabKey,
    localValueRef: React.RefObject<BlueConicRecommendedNotificationParams>,
    notifEditorRef: React.RefObject<NodeEditorRef>,
    setter: (value: React.SetStateAction<ActiveTabKey>) => void,
) {
    if (nextTabKey === currTabKey) return

    if (nextTabKey === 'configuration') {
        const notifUpdates = await handleNotificationSubmit(notifEditorRef.current)
        if (notifUpdates) {
            setter(nextTabKey)
        }
    } else {
        const configUpdates = await handleConfigurationSubmit(localValueRef.current!)
        if (configUpdates) {
            setter(nextTabKey)
        }
    }
}
